import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "@env";
import { PageManager } from "@shared/models/page-manager";

export interface FranchiseResponseDTO {
  id: number;
  name: string;
  description: string;
  backofficeTypes: string[];
}

export interface Page<T> {
  content: T[];
  totalElements: number;
}

export interface FranchiseNegotiationDTO {
  franchiseIds: number[];
  negotiationId: number;
}

@Injectable({
  providedIn: "root"
})
export class FranchiseNegotiationService {
  private baseUrl = `${environment.apiUrl}franchise`;

  constructor(private http: HttpClient) {}

  listWithFilters(params: {
    page?: number;
    size?: number;
    sort?: string;
    name?: string;
    description?: string;
    backofficeTypes?: string[];
  }): Observable<Page<FranchiseResponseDTO>> {
    let hp = new HttpParams();
    if (params.page != null) hp = hp.set("page", params.page.toString());
    if (params.size != null) hp = hp.set("size", params.size.toString());
    if (params.sort) hp = hp.set("sort", params.sort);
    if (params.name) hp = hp.set("name", params.name);
    if (params.description) hp = hp.set("description", params.description);
    if (params.backofficeTypes?.length) {
      params.backofficeTypes.forEach((t) => {
        hp = hp.append("backofficeTypes", t);
      });
    }
    return this.http.get<Page<FranchiseResponseDTO>>(this.baseUrl, {
      params: hp
    });
  }

  getByNegotiation(
    negotiationId: number,
    page: PageManager,
    sort: string = ""
  ): Observable<Page<FranchiseResponseDTO>> {
    const url = `${this.baseUrl}/negotiation/${negotiationId}`;
    const params = new HttpParams()
      .set("page", page.pageIndex.toString())
      .set("size", page.pageSize.toString())
      .set("sort", sort);
    return this.http.get<Page<FranchiseResponseDTO>>(url, { params });
  }

  listByNegotiation(
    negotiationId: number,
    params: {
      page?: number;
      size?: number;
      sort?: string;
      name?: string;
    }
  ): Observable<Page<FranchiseResponseDTO>> {
    let hp = new HttpParams()
      .set("negotiationId", negotiationId.toString())
      .set("page", (params.page ?? 0).toString())
      .set("size", (params.size ?? 30).toString());
    if (params.sort) hp = hp.set("sort", params.sort);
    if (params.name) hp = hp.set("name", params.name);
    return this.http.get<Page<FranchiseResponseDTO>>(
      `${this.baseUrl}/negotiation`,
      { params: hp }
    );
  }

  removeNegotiation(
    franchiseId: number,
    negotiationId: number
  ): Observable<void> {
    const params = new HttpParams()
      .set("franchiseId", franchiseId.toString())
      .set("negotiationId", negotiationId.toString());
    return this.http.patch<void>(`${this.baseUrl}/remove-negotiation`, null, {
      params
    });
  }

  addNegotiation(dto: FranchiseNegotiationDTO): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/add-negotiation`, dto);
  }

  getFranchisesAutocomplete(
    negotiationId: number,
    name: string
  ): Observable<FranchiseResponseDTO[]> {
    const params = new HttpParams()
      .set("negotiationId", negotiationId.toString())
      .set("name", name);
    return this.http.get<FranchiseResponseDTO[]>(
      `${this.baseUrl}/negotiation-autocomplete`,
      { params }
    );
  }
}
